import {
  cn,
  Select as V2Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@foresightdata/web-common-components';
import { MenuItem as MuiMenuItem, Select as MuiSelect, SelectProps as MuiSelectProps } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Children, isValidElement, ReactElement, ReactNode } from 'react';
import { MaggieFeatureFlags } from '../../util/feature-flags';

/**
 * The app's Select and SelectOption (FD-6112): a picker without a text input (for a filterable
 * list see Autocomplete). MUI today; web-common's Select (Base UI) with the flag on. Options are
 * `SelectOption` children (MUI's `MenuItem value`), so the wrapper can hand Base UI their labels.
 * They must be direct children (or an array): options inside a Fragment are not seen, and the
 * trigger would show the raw value.
 *
 * Deliberately the props the app uses: `onChange` gets the value only; `displayEmpty` with
 * `renderValue` shows a placeholder for the empty value; `size` is accepted for v1 (one size in
 * v2). No `MenuProps`, `IconComponent`, `sx`; `inputProps['aria-label']` is `aria-label`.
 */
export type SelectValueType = string | number;

export interface SelectProps extends Pick<
  MuiSelectProps<SelectValueType>,
  | 'value'
  | 'disabled'
  | 'displayEmpty'
  | 'size'
  | 'name'
  | 'id'
  | 'className'
  | 'style'
  | 'aria-label'
  | 'fullWidth'
> {
  onChange?: (value: SelectValueType) => void;
  /** What the trigger shows for a value (MUI's `renderValue`); also the placeholder for the empty value. */
  renderValue?: (value: SelectValueType | '') => ReactNode;
  children: ReactNode;
}

export interface SelectOptionProps {
  value: SelectValueType | '';
  disabled?: boolean;
  children: ReactNode;
  className?: string;
}

const EMPTY = '';

export function Select({
  value,
  onChange,
  disabled,
  displayEmpty,
  renderValue,
  size,
  fullWidth,
  className,
  children,
  'aria-label': ariaLabel,
  id,
  style,
  ...rest
}: SelectProps) {
  const { useV2Components } = useFlags<MaggieFeatureFlags>();

  if (!useV2Components) {
    return (
      <MuiSelect<SelectValueType>
        value={value}
        onChange={onChange ? (event) => onChange(event.target.value as SelectValueType) : undefined}
        disabled={disabled}
        displayEmpty={displayEmpty}
        renderValue={renderValue}
        size={size}
        fullWidth={fullWidth}
        className={className}
        id={id}
        style={style}
        inputProps={ariaLabel ? { 'aria-label': ariaLabel } : undefined}
        {...rest}
      >
        {children}
      </MuiSelect>
    );
  }

  // Base UI shows an item's label in the trigger only if it knows the labels up front.
  const items = Object.fromEntries(
    Children.toArray(children)
      .filter(
        (child): child is ReactElement<SelectOptionProps> =>
          isValidElement(child) && child.props != null && 'value' in (child.props as object)
      )
      .map((child) => [
        String(child.props.value),
        renderValue ? renderValue(child.props.value) : child.props.children,
      ])
  );
  const current = value === EMPTY || value === undefined ? null : value;
  const placeholder = displayEmpty ? (renderValue ? renderValue(EMPTY) : items[EMPTY]) : undefined;

  return (
    <V2Select
      // web-common's Select is typed for string values; numbers round-trip through `items` keys.
      value={current as string | null}
      onValueChange={(next) => onChange?.((next ?? EMPTY) as SelectValueType)}
      disabled={disabled}
      items={items}
      {...rest}
    >
      {/* The root renders no element: what addresses the field goes on the trigger. */}
      <SelectTrigger
        className={cn(fullWidth && 'w-full', className)}
        aria-label={ariaLabel}
        id={id}
        style={style}
      >
        <SelectValue placeholder={placeholder}>
          {renderValue ? (next: SelectValueType | null) => renderValue(next ?? EMPTY) : undefined}
        </SelectValue>
      </SelectTrigger>
      {/* MUI drops the menu below the field rather than over it. */}
      <SelectContent alignItemWithTrigger={false} align='start'>
        {children}
      </SelectContent>
    </V2Select>
  );
}

export function SelectOption({ value, disabled, children, className, ...rest }: SelectOptionProps) {
  const { useV2Components } = useFlags<MaggieFeatureFlags>();

  if (!useV2Components) {
    // MUI's Select clones its options with the selection props; `rest` carries them through.
    return (
      <MuiMenuItem value={value} disabled={disabled} className={className} {...rest}>
        {children}
      </MuiMenuItem>
    );
  }

  // The empty value is Base UI's null item.
  return (
    <SelectItem value={value === EMPTY ? null : value} disabled={disabled} className={className}>
      {children}
    </SelectItem>
  );
}
